import InfoTooltip from 'components/InfoTooltip';
import { Tooltip } from 'components/ui/tooltip';
import { formatToHours } from 'components/util/HoursDisplay';
import { FormattedMessage } from 'react-intl';
import { PieChartItem } from 'types/dashboard';
import type { HomeWidgetSize } from 'types/homeWidgets';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';

type Props = {
    data: PieChartItem[];
    /** Home's legend: one row per series by the ring, the value in tabular figures, the explanation on the label's hover. */
    home?: boolean;
    /**
     * Home's card size. Two columns wide the legend stops at 288px, keeping each value by its label; four rows tall
     * it runs the card's width under the ring, in ruled rows.
     */
    size?: HomeWidgetSize;
};

function FinalizedHoursLegend({ data, home = false, size }: Props) {
    const { displayDecimalScale } = useTimecardPrecision();
    if (home) {
        const tall = size === '1x4';
        return (
            <ul
                className={cn(
                    'flex min-w-0 flex-col',
                    tall ? 'self-stretch' : 'flex-1 gap-1.5',
                    size === '2x2' && 'max-w-72'
                )}
            >
                {data.map((item) => {
                    const label = (
                        <span className="text-foreground-secondary min-w-0 flex-1 truncate">{item.label}</span>
                    );
                    return (
                        <li
                            key={item.id}
                            className={cn(
                                'text-meta flex items-center gap-2',
                                tall && 'border-divider-soft h-[27px] border-t'
                            )}
                        >
                            <svg aria-hidden width="8" height="8" viewBox="0 0 8 8" className="shrink-0">
                                <rect width="8" height="8" rx="2" fill={item.color} />
                            </svg>
                            {item.tooltip ? <Tooltip content={t(item.tooltip)}>{label}</Tooltip> : label}
                            <span className="font-medium whitespace-nowrap tabular-nums">
                                {formatToHours(item.value, displayDecimalScale)}
                            </span>
                        </li>
                    );
                })}
            </ul>
        );
    }
    return (
        <div className="mt-5 flex flex-row flex-wrap justify-center gap-x-5 gap-y-2">
            {data.map((item) => (
                <div key={item.id} className="flex flex-row items-center gap-1 whitespace-nowrap">
                    <svg width="10" height="10" viewBox="0 0 10 10">
                        <circle cx="5" cy="5" r="5" fill={item.color}></circle>
                    </svg>
                    <FormattedMessage
                        id="key-value-semicolon-separated"
                        values={{
                            key: item.label,
                            value: formatToHours(item.value, displayDecimalScale),
                        }}
                    />
                    {item.tooltip !== undefined && <InfoTooltip text={item.tooltip} color="primary" />}
                </div>
            ))}
        </div>
    );
}
export default FinalizedHoursLegend;
